// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import type { PolicyDetail } from '../../shared/types/procedures';
import StatusTag from './StatusTag';
import styles from './ProcedurePreview.module.css';
import policyStyles from './PolicyPreview.module.css';

interface PolicyPreviewProps {
  defaultPreviewTitle?: string;
  policy: PolicyDetail;
}

function PreviewSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionLabel}>{label}</div>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>{label}</span>
      <span className={value ? styles.rowValue : styles.rowValueEmpty}>{value || '—'}</span>
    </div>
  );
}

/**
 * Read-rendered "live preview" of a policy — the policy-as-code counterpart
 * of `ProcedurePreview`, reusing its CSS module (same look, different
 * content: no steps/equipment/governance, just the Rego package pointer).
 */
export default function PolicyPreview({ policy, defaultPreviewTitle }: PolicyPreviewProps) {
  return (
    <div className={styles.preview}>
      <div className={styles.liveBar}>
        <span className={styles.liveDot} />
        <span className={styles.liveLabel}>LIVE PREVIEW</span>
      </div>
      <div className={styles.body}>
        <div className={styles.header}>
          <div className={styles.title}>
            {policy.title || defaultPreviewTitle || 'Untitled policy'}
          </div>
          <div className={styles.subline}>
            <span className={styles.mono}>{policy.policyNumber}</span>
            {policy.version && <span className={styles.mono}> / v{policy.version}</span>}
            <StatusTag status={policy.status} />
          </div>
          <div className={policyStyles.chipsGrid}>
            <span className={styles.chip}>{policy.classification || 'Classification not set'}</span>
            <span className={styles.chip}>{policy.policyDomain || 'Policy domain not set'}</span>
            <span className={styles.chip}>{policy.scope || 'Scope not set'}</span>
            <span className={styles.chip}>
              {policy.enforcementPoint || 'Enforcement point not set'}
            </span>
          </div>
        </div>

        <div className={styles.description}>
          {policy.decisionSummary || 'No decision summary yet.'}
        </div>

        <PreviewSection label="Policy as code">
          <Row label="Package" value={policy.package} />
          <Row label="Language" value="rego" />
          <Row label="Policy file" value={policy.files.policy} />
          <Row label="Data file" value={policy.files.data} />
          <Row label="Tests file" value={policy.files.tests} />
        </PreviewSection>
      </div>
    </div>
  );
}
